import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';

import { usePrev } from '@libs/hooks/usePrev';

import { AuthCookie } from '@features/auth/services/AuthCookie';
import { isBlacklistedUrl } from '@features/auth/utils/redirect';
import { useMeQuery, useTokenRefreshMutation } from '@features/shared/api';

import { AuthService } from '../../services/AuthService';
import { AuthContext, IAuthContext } from './AuthContext';

const TOKEN_AUTH_PATH = '/auth/by-token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const ql = useQueryClient();
  const router = useRouter();

  const auth = AuthService.instance;
  const cookie = AuthCookie.instance;

  // локальная и глобальная учётки должны совпадать
  const refIsRedirectingToAuth = useRef(false);
  const isAuthGloballySynced = (() => {
    if (
      __RUNTIME.mode === 'development' ||
      !cookie.chessOpenId ||
      router.latestLocation.pathname === TOKEN_AUTH_PATH
    )
      return true;

    if (refIsRedirectingToAuth.current) return false;

    if (cookie.chessOpenId === 'NONE') return !auth.isAuthenticated;

    return Boolean(
      auth.isAuthenticated && auth.user && String(cookie.chessOpenId) === String(auth.user.userId),
    );
  })();

  // синхронизируем учётки (sic! синхронно в время отрисовки, до основной логики)
  if (!isAuthGloballySynced && !refIsRedirectingToAuth.current) {
    // сбросим текущую локальную учётку
    if (auth.isAuthenticated) {
      auth.deleteTokens();
    }

    // логиним пользователя под глобальной учёткой
    if (cookie.chessOpenId && cookie.chessOpenId !== 'NONE') {
      refIsRedirectingToAuth.current = true;
      navigateToAutoLogin(router.latestLocation.href);
    }
  }

  const { isAuthenticated, status } = auth;
  const [, setVer] = useState(0);
  const prevAuthenticated = usePrev(isAuthenticated);

  // Обновить пользовательские данные
  useMeQuery({
    enabled: auth.isAuthenticated && isAuthGloballySynced && auth.status !== 'refresh', // sic! без нас обновится
  });

  useEffect(() => {
    if (!isAuthenticated && prevAuthenticated) {
      // Инвалидация кэша при разлогине. При логине кэш инвалидирует useLogin
      navigateToSessionExpired(router, router.latestLocation.href);
      ql.removeQueries();
      ql.invalidateQueries();
    } else if (auth.expiredInBackgound) {
      // Показать страницу логина тем, у кого протух токен пока они не заходили на сайт
      navigateToSessionExpired(router, router.latestLocation.href);
    }

    // Триггерим обновление контекста
    return auth.subscribe(() => setVer((v) => v + 1));
  }, [auth, isAuthenticated, prevAuthenticated, ql, router]);

  // Синхронизация при возврате на вкладку
  useEffect(() => {
    const handleFocus = () => {
      const prevCookie = cookie.chessOpenId;

      cookie.syncFromCookie();
      auth.syncFromLocalStorage();

      if (prevCookie !== cookie.chessOpenId) {
        setVer((v) => v + 1);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [auth, cookie]);

  // Обновление токена
  const refreshMutation = useTokenRefreshMutation();
  useEffect(() => {
    if (status === 'refresh') {
      if (refreshMutation.isIdle) {
        refreshMutation.mutate();
      } else if (refreshMutation.isError) {
        auth.deleteTokens();
      }
    }
  }, [refreshMutation, status, auth]);

  const value: IAuthContext = {
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,

    logout: useCallback(
      (back?: string) => {
        const autoBack = router.latestLocation.href;
        navigateToSessionExpired(router, back ?? autoBack);
        setTimeout(() => {
          auth.deleteTokens();
          cookie.setLoggedOut();
        }, 0);
      },
      [auth, cookie, router],
    ),

    login: useCallback(
      (back?: string) => {
        const autoBack = router.latestLocation.href;

        if (__RUNTIME.mode === 'development') {
          router.navigate({
            from: '/',
            to: '/login',
            search: { back: back ?? autoBack },
          });
        } else {
          navigateToChessOpenId(back ?? autoBack);
        }
      },
      [router],
    ),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function navigateToSessionExpired(router: ReturnType<typeof useRouter>, back?: string) {
  if (isBlacklistedUrl(router.latestLocation.pathname)) {
    return;
  }

  router.navigate({
    from: '/',
    to: '/auth/end',
    search: back && !isBlacklistedUrl(back) ? { back } : undefined,
  });
}

function navigateToChessOpenId(back?: string) {
  window.location.href = [
    `${__RUNTIME.idUrl}/?source=PlayChessOpen`,
    back && !isBlacklistedUrl(back) ? `back=${back}` : null,
  ]
    .filter(Boolean)
    .join('&');
}

function navigateToAutoLogin(back?: string) {
  window.location.href = [
    `${__RUNTIME.idUrl}/auth/token-issue?audience=PlayChessOpen`,
    back && !isBlacklistedUrl(back) ? `back=${back}` : null,
  ]
    .filter(Boolean)
    .join('&');
}
